/**
 * **When the trainer works, day by day** — booking-system R3 and R4, projected onto the days the
 * grid shows.
 *
 * R3: a weekly grid of working windows per ISO weekday, and any single date may be OVERRIDDEN with
 * other windows or with none (closed). R4: a block is one absolute time range the trainer is not
 * bookable in, visible to the trainer only. Everything outside a day's windows is "Poza grafikiem",
 * which the grid hatches.
 *
 * The windows are wall readings on the trainer's clock, and the grid is drawn on that clock, so a
 * window goes to FullCalendar as the same reading it came in as. It goes as a ONE-DAY recurrence
 * (`daysOfWeek` + `startRecur`/`endRecur` spanning that date alone): `businessHours` accepts only
 * recurring definitions, and expanding every date separately is what lets an override replace its
 * weekday's windows on that one date and nowhere else.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesBookingDtosResponsesTrainerAvailabilityResponse as TrainerAvailabilityResponse } from '~/shared/api/generated/model';
import { parseInstant } from '~/shared/lib/calendarDate';

import {
  clockOf,
  isoWeekday,
  paddedClock,
  minutesOfTime,
  shiftDay,
  wallReading,
  type CivilDay,
} from './trainerClock';

/** One working window, in minutes since the day's midnight. `end` may be 1440 — midnight after. */
export interface WorkingWindow {
  readonly start: number;
  readonly end: number;
}

/** Where a day's windows came from — the grid's day header says the two exceptional cases. */
export type WorkingSource = 'weekly' | 'override';

export interface WorkingDay {
  readonly day: CivilDay;
  readonly source: WorkingSource;
  /** Empty = the trainer does not work that day (a free weekday, or an override closing it). */
  readonly windows: readonly WorkingWindow[];
}

/** One of the trainer's blocks, read back as instants. */
export interface TrainerBlock {
  readonly id: number;
  readonly startMs: number;
  readonly endMs: number;
  /** The trainer's own note, trimmed; `null` for none. */
  readonly note: string | null;
}

/** What FullCalendar's `businessHours` takes for one date — plain data, no library type. */
export interface BusinessHoursEntry {
  /** JavaScript's weekday, 0 (Sunday) … 6 — FullCalendar's convention, not ISO's. */
  readonly daysOfWeek: readonly number[];
  readonly startTime: string;
  readonly endTime: string;
  readonly startRecur: CivilDay;
  readonly endRecur: CivilDay;
}

/** What the day header's second line says, if anything. */
export type DayNote =
  | { readonly kind: 'override'; readonly range: string }
  | { readonly kind: 'overrideClosed' }
  | { readonly kind: 'block'; readonly range: string }
  | { readonly kind: 'free' }
  | { readonly kind: 'none' };

const DAY_MINUTES = 1440;

function readWindow(start: string | undefined, end: string | undefined): WorkingWindow | null {
  const from = minutesOfTime(start);
  const until = minutesOfTime(end);
  if (from === null || until === null) return null;
  // A window cannot cross midnight (R3), so an end at or before its start can only be midnight.
  const to = until <= from ? (until === 0 ? DAY_MINUTES : null) : until;
  return to === null ? null : { start: from, end: to };
}

function sortWindows(windows: WorkingWindow[]): WorkingWindow[] {
  return windows.sort((a, b) => a.start - b.start);
}

/** The trainer's windows on each of `days`: the date's override where one exists, else its weekday's. */
export function workingDays(
  availability: TrainerAvailabilityResponse,
  days: readonly CivilDay[],
): WorkingDay[] {
  const weekly = new Map<number, WorkingWindow[]>();
  for (const entry of availability.weekly ?? []) {
    const window = readWindow(entry.start, entry.end);
    if (window === null || entry.dayOfWeek === undefined) continue;
    weekly.set(entry.dayOfWeek, [...(weekly.get(entry.dayOfWeek) ?? []), window]);
  }
  const overrides = new Map<CivilDay, WorkingWindow[]>();
  for (const entry of availability.overrides ?? []) {
    if (!entry.date) continue;
    const windows = (entry.windows ?? [])
      .map((window) => readWindow(window.start, window.end))
      .filter((window): window is WorkingWindow => window !== null);
    overrides.set(entry.date.slice(0, 10), windows);
  }

  return days.map((day) => {
    const override = overrides.get(day);
    if (override !== undefined) return { day, source: 'override', windows: sortWindows(override) };
    return { day, source: 'weekly', windows: sortWindows([...(weekly.get(isoWeekday(day)) ?? [])]) };
  });
}

/** FullCalendar's `businessHours` for exactly these days. A day with no windows contributes nothing. */
export function businessHoursOf(days: readonly WorkingDay[]): BusinessHoursEntry[] {
  return days.flatMap((working) =>
    working.windows.map((window) => ({
      daysOfWeek: [isoWeekday(working.day) % 7],
      startTime: paddedClock(window.start),
      endTime: paddedClock(window.end),
      startRecur: working.day,
      endRecur: shiftDay(working.day, 1),
    })),
  );
}

/** The blocks, as instants. A block the wire could not state as a range is dropped, not guessed. */
export function trainerBlocks(availability: TrainerAvailabilityResponse): TrainerBlock[] {
  return (availability.blocks ?? []).flatMap((block) => {
    const startMs = parseInstant(block.startsAt);
    const endMs = parseInstant(block.endsAt);
    if (startMs === null || endMs === null || endMs <= startMs || block.id === undefined) return [];
    return [{ id: block.id, startMs, endMs, note: block.note?.trim() || null }];
  });
}

/**
 * The part of a block that falls on `day`, in minutes, or `null` when it misses the day.
 * A block running past midnight reads to 24:00 on its first day and from 0:00 on the next.
 */
export function blockSpanOn(
  block: TrainerBlock,
  day: CivilDay,
  zone: string,
): WorkingWindow | null {
  const from = wallReading(block.startMs, zone);
  const to = wallReading(block.endMs, zone);
  if (from.day > day || to.day < day) return null;
  const start = from.day === day ? from.minutes : 0;
  const end = to.day === day ? to.minutes : DAY_MINUTES;
  return end > start ? { start, end } : null;
}

/** `16–18`, or `9:30–12` — the canon's compact span: whole hours drop their minutes. */
export function compactSpan(window: WorkingWindow): string {
  const read = (minutes: number) =>
    minutes % 60 === 0 ? String(Math.floor(minutes / 60)) : clockOf(minutes);
  return `${read(window.start)}–${read(window.end)}`;
}

/**
 * The day header's second line. One line, so one fact, in this order: an override is the day
 * being unlike every other of its weekday, and the thing a trainer most needs to see; then a block
 * that day; then a day with no hours at all.
 */
export function dayNoteOf(
  working: WorkingDay,
  blocks: readonly TrainerBlock[],
  zone: string,
): DayNote {
  if (working.source === 'override') {
    return working.windows.length === 0
      ? { kind: 'overrideClosed' }
      : { kind: 'override', range: working.windows.map(compactSpan).join(', ') };
  }
  for (const block of blocks) {
    const span = blockSpanOn(block, working.day, zone);
    if (span !== null) return { kind: 'block', range: compactSpan(span) };
  }
  return working.windows.length === 0 ? { kind: 'free' } : { kind: 'none' };
}
